import { Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { PropsWithChildren } from "react";
import {
  Drawer,
  DrawerActions,
  DrawerContent,
  DrawerHeader,
} from "@/components/common/drawer";
import Modal from "@/components/common/modals/modal";
import { CrossIcon } from "./icons";

type Props = PropsWithChildren<{
  open: boolean;
  title: string;
  description: string;
  cancelButtonText: string;
  confirmButtonText: string;
  submitDisabled?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  hideDivider?: boolean;
}>;

/**
 * DrawerModalForm is a component that renders a drawer or modal form based on the screen size.
 * The form is passed as children, the drawerModal have two buttons, one for cancel and one for confirm.
 */
export const DrawerModalForm = (props: Props) => {
  const theme = useTheme();

  const isMobile = useMediaQuery<typeof theme>((theme) =>
    theme.breakpoints.down("md")
  );

  if (isMobile) {
    return <DrawerForm {...props} />;
  }

  return <ModalForm {...props} />;
};

const DrawerForm = ({
  children,
  open,
  title,
  description,
  cancelButtonText,
  confirmButtonText,
  submitDisabled,
  hideDivider,
  onClose,
  onSubmit,
}: Props) => {
  const theme = useTheme();

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader
        title={title}
        onCloseAction={onClose}
        HeaderProps={{ sx: { py: 2, mt: 1 } }}
        TitleProps={{ sx: { textAlign: "center" } }}
        CloseActionBoxProps={{
          sx: { position: "absolute", right: theme.spacing(1) },
        }}
      />
      <DrawerContent sx={{ pt: 1, px: 2 }}>
        <Typography
          variant="paragraph"
          align="center"
          sx={{ color: "text.secondary", mb: 2 }}
        >
          {description}
        </Typography>
        {children}
      </DrawerContent>
      <DrawerActions hideDivider={hideDivider}>
        <Button fullWidth variant="outlined" onClick={onClose}>
          {cancelButtonText}
        </Button>
        <Button
          fullWidth
          variant="contained"
          disabled={submitDisabled}
          onClick={onSubmit}
        >
          {confirmButtonText}
        </Button>
      </DrawerActions>
    </Drawer>
  );
};

// @TODO: We should be using Dialog component where possible not modal.
const ModalForm = ({
  children,
  open,
  onClose,
  onSubmit,
  submitDisabled,
  title,
  description,
  cancelButtonText,
  confirmButtonText,
}: Props) => {
  const theme = useTheme();

  return (
    <Modal
      open={open}
      handleClose={onClose}
      confirm={onSubmit}
      confirmDisabled={submitDisabled}
      discard={onClose}
      title={title}
      description={description}
      discardButtonText={cancelButtonText}
      confirmButtonText={confirmButtonText}
      sx={{
        p: 2,
        minWidth: "400px",
        minHeight: "auto",
      }}
      TitleProps={{ sx: { textAlign: "left" } }}
      CloseIconButtonProps={{
        sx: {
          top: theme.spacing(1),
          right: theme.spacing(1),
        },
      }}
      closeIcon={<CrossIcon size={24} color="primary" />}
      DescriptionProps={{
        sx: {
          mb: 2,
          textAlign: "left",
        },
      }}
      ButtonProps={{
        size: "small",
        sx: {
          flex: 1,
          py: 1,
          height: "auto",
        },
      }}
      childrenWrapperSx={{ pb: 2 }}
    >
      {children}
    </Modal>
  );
};
